import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CONSULT_FORM_SUBMIT_ADDR, LINE_OFFICIAL_URL } from '@/pages/gmb-audit/consult/config';
import LineCtaButton from '@/pages/gmb-audit/consult/components/LineCtaButton';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

// 目前最想改善的問題（可複選）。
const PROBLEM_OPTIONS = [
  'Google 商家資料完整度',
  'Google 評論與信譽',
  'Google 商家曝光與同業競爭',
  '品牌官網',
  'LINE 官方帳號',
  '社群／短影音',
  'AI 智慧客服',
  '數位接單流程',
  '整合行銷',
  '不確定，希望顧問協助判斷',
];

// 「不確定，希望顧問協助判斷」為排他選項（僅 UI selection rule）。
const UNCERTAIN_OPTION = '不確定，希望顧問協助判斷';
const PROBLEM_SEPARATOR = '、';

// 方便聯絡時段（單選）。
const CONTACT_TIME_OPTIONS = ['平日上午', '平日下午', '平日晚間', '假日', '時間皆可'];

const FIELD_BASE =
  'h-[54px] w-full rounded-xl border border-background-400 bg-background-50 px-4 text-base text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1.5 text-sm text-rose-600">
      <i className="ri-error-warning-line text-base" />
      {message}
    </p>
  );
}

/**
 * 諮詢需求表單（V1 Conversion-1）。
 * 送出後以 application/x-www-form-urlencoded POST 到 Readdy 內建 Form；
 * 提供中文必填錯誤、送出中 loading、防重複送出、成功卡與錯誤提示。
 */
export default function ConsultForm({ defaultStoreName = '' }: { defaultStoreName?: string }) {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [issues, setIssues] = useState<string[]>([]);
  const [contactTime, setContactTime] = useState('');
  const [consent, setConsent] = useState(false);

  // 複選切換 + 「不確定」排他規則（僅 frontend UI selection rule）。
  const toggleIssue = (option: string) => {
    setErrors((prev) => ({ ...prev, mainProblem: '' }));
    setIssues((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option);
      }
      if (option === UNCERTAIN_OPTION) {
        return [UNCERTAIN_OPTION];
      }
      return [...prev.filter((item) => item !== UNCERTAIN_OPTION), option];
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const formEl = event.currentTarget;
    // 立即以實際 DOM 讀值（含 AutoFill），不依賴 React state。
    const formData = new FormData(formEl);

    // 反垃圾：蜜罐欄位若有值，不送出，僅顯示一般成功回饋。
    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    const value = (key: string) => String(formData.get(key) || '').trim();

    const nextErrors: Record<string, string> = {};
    if (!value('name')) nextErrors.name = '請填寫姓名／稱呼';
    if (!value('storeName')) nextErrors.storeName = '請填寫店家／公司名稱';
    if (!value('phone')) nextErrors.phone = '請填寫聯絡電話';
    if (issues.length < 1) nextErrors.mainProblem = '請至少選擇一項目前最想改善的問題。';
    if (!formData.get('consent')) nextErrors.consent = '請勾選聯絡同意';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('idle');
      return;
    }

    setErrors({});
    setFormError('');
    setStatus('submitting');

    try {
      const payload = new URLSearchParams();
      formData.forEach((val, key) => {
        if (key === 'website_alt') return;
        if (key === 'mainProblem') return; // 多選欄位改由 state 整理後送出
        if (typeof val === 'string' && val.trim() === '') return;
        payload.append(key, val as string);
      });
      // 多選整理為人類可直接閱讀的繁體中文文字（不送 JSON / array / [object Object]）。
      if (issues.length > 0) {
        payload.append('mainProblem', issues.join(PROBLEM_SEPARATOR));
      }

      const response = await fetch(CONSULT_FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setFormError(
          String(serverMsg) || '目前系統暫時無法完成送出，請稍後再試。',
        );
        setStatus('error');
        return;
      }

      setStatus('success');
      formEl.reset();
    } catch {
      setFormError('網路連線異常，請稍後再試。');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-2xl border border-primary-200 bg-primary-50/70 p-6 shadow-card md:p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 text-primary-800">
          <i className="ri-checkbox-circle-line text-2xl" />
        </span>
        <h2 className="mt-4 text-xl font-bold text-foreground-950 md:text-2xl">已收到你的諮詢需求</h2>
        <p className="mt-2.5 text-base leading-relaxed text-foreground-600">
          天地人網會依你提供的資訊與你聯絡，協助你進一步釐清目前最適合優先改善的方向。
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {LINE_OFFICIAL_URL && <LineCtaButton variant="primary" fullWidth className="sm:w-auto" />}
          <Link
            to="/gmb-audit"
            className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-background-400 bg-background-50 px-6 text-base font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:w-auto"
          >
            <i className="ri-arrow-go-back-line text-lg" />
            返回 Google 商家快速健診
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      id="gmb-audit-consult-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-6"
    >
      {/* 姓名 / 店家 */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="consult-name" className="text-base font-medium text-foreground-700">
            姓名／稱呼 <span className="text-rose-600">*</span>
          </label>
          <input
            id="consult-name"
            name="name"
            type="text"
            maxLength={60}
            className={FIELD_BASE}
            placeholder="你的姓名或稱呼"
            aria-invalid={Boolean(errors.name)}
          />
          <FieldError message={errors.name} />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="consult-store" className="text-base font-medium text-foreground-700">
            店家／公司名稱 <span className="text-rose-600">*</span>
          </label>
          <input
            id="consult-store"
            name="storeName"
            type="text"
            maxLength={80}
            defaultValue={defaultStoreName}
            className={FIELD_BASE}
            placeholder="想改善的店家或公司名稱"
            aria-invalid={Boolean(errors.storeName)}
          />
          <FieldError message={errors.storeName} />
        </div>
      </div>

      {/* 電話 / Email */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="consult-phone" className="text-base font-medium text-foreground-700">
            聯絡電話 <span className="text-rose-600">*</span>
          </label>
          <input
            id="consult-phone"
            name="phone"
            type="tel"
            maxLength={30}
            className={FIELD_BASE}
            placeholder="方便與你聯絡的電話"
            aria-invalid={Boolean(errors.phone)}
          />
          <FieldError message={errors.phone} />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="consult-email" className="text-base font-medium text-foreground-700">
            Email <span className="text-sm font-normal text-foreground-400">（選填）</span>
          </label>
          <input
            id="consult-email"
            name="email"
            type="email"
            maxLength={120}
            className={FIELD_BASE}
            placeholder="you@example.com"
          />
        </div>
      </div>

      {/* LINE ID / 縣市 */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="consult-line" className="text-base font-medium text-foreground-700">
            LINE ID <span className="text-sm font-normal text-foreground-400">（選填）</span>
          </label>
          <input
            id="consult-line"
            name="lineId"
            type="text"
            maxLength={60}
            className={FIELD_BASE}
            placeholder="若方便透過 LINE 聯絡，可留下 LINE ID"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="consult-city" className="text-base font-medium text-foreground-700">
            所在縣市 <span className="text-sm font-normal text-foreground-400">（選填）</span>
          </label>
          <input
            id="consult-city"
            name="city"
            type="text"
            maxLength={40}
            className={FIELD_BASE}
            placeholder="例如：台中市"
          />
        </div>
      </div>

      {/* 最想改善的問題（可複選） */}
      <div className="flex flex-col gap-2.5">
        <span className="text-base font-medium text-foreground-700">
          目前最想改善的問題（可複選） <span className="text-rose-600">*</span>
        </span>
        <p className="text-base leading-relaxed text-foreground-500">可複選，請至少選擇 1 項。</p>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {PROBLEM_OPTIONS.map((option) => {
            const selected = issues.includes(option);
            return (
              <label
                key={option}
                className={`flex min-h-[56px] cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 text-base transition-colors ${
                  selected
                    ? 'border-primary-500 bg-primary-50 text-foreground-950'
                    : 'border-background-400 bg-background-50 text-foreground-700 hover:bg-background-100'
                }`}
              >
                <input
                  type="checkbox"
                  name="mainProblem"
                  value={option}
                  checked={selected}
                  onChange={() => toggleIssue(option)}
                  className="h-5 w-5 shrink-0 cursor-pointer accent-primary-700"
                />
                <span className="leading-snug">{option}</span>
              </label>
            );
          })}
        </div>
        <FieldError message={errors.mainProblem} />
      </div>

      {/* 方便聯絡時段 */}
      <div className="flex flex-col gap-2">
        <label htmlFor="consult-time" className="text-base font-medium text-foreground-700">
          方便聯絡時段 <span className="text-sm font-normal text-foreground-400">（選填）</span>
        </label>
        <select
          id="consult-time"
          name="contactTime"
          value={contactTime}
          onChange={(e) => setContactTime(e.target.value)}
          className={`${FIELD_BASE} cursor-pointer`}
        >
          <option value="">請選擇（可留空）</option>
          {CONTACT_TIME_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* 其他需求 */}
      <div className="flex flex-col gap-2">
        <label htmlFor="consult-message" className="text-base font-medium text-foreground-700">
          其他需求／想補充的問題 <span className="text-sm font-normal text-foreground-400">（選填）</span>
        </label>
        <textarea
          id="consult-message"
          name="message"
          rows={5}
          maxLength={500}
          className="min-h-[140px] w-full resize-none rounded-xl border border-background-400 bg-background-50 px-4 py-3.5 text-base text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          placeholder="例如：目前主要客源、想先處理的方向，或任何想先讓我們知道的狀況。"
        />
        <p className="text-sm text-foreground-400">最多 500 字。</p>
      </div>

      {/* 個資同意 */}
      <div className="flex flex-col gap-2">
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-background-400 bg-background-50 px-4 py-3.5">
          <input
            type="checkbox"
            name="consent"
            value="yes"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              setErrors((prev) => ({ ...prev, consent: '' }));
            }}
            className="mt-0.5 h-5 w-5 shrink-0 accent-primary-700"
          />
          <span className="text-base leading-relaxed text-foreground-700">
            我同意天地人網依本次諮詢需求與我聯絡，所提供資料僅用於回覆與後續服務聯繫。
            <span className="text-rose-600"> *</span>
          </span>
        </label>
        <FieldError message={errors.consent} />
        <p className="text-base leading-relaxed text-foreground-600">
          送出前可先閱讀
          <Link
            to="/gmb-audit/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="mx-1 font-semibold text-primary-700 underline underline-offset-2 transition-colors hover:text-primary-800"
          >
            隱私權與個資蒐集說明
          </Link>
          。
        </p>
      </div>

      {/* 反垃圾蜜罐欄位（以全站 CSS 隱藏，非 inline style / hidden / sr-only） */}
      <div className="aux-field-wrap" aria-hidden="true">
        <input
          id="consult-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      {status === 'error' && formError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <p className="flex items-center gap-2 font-semibold text-rose-700">
            <i className="ri-error-warning-line text-lg" />
            諮詢需求暫時無法送出
          </p>
          <p className="mt-1.5 text-base leading-relaxed text-rose-700">
            目前系統暫時無法完成送出，請稍後再試，或直接透過 LINE 與天地人網聯絡。
          </p>
          <div className="mt-4">
            <LineCtaButton fullWidth className="sm:w-auto" />
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 sm:self-start sm:px-8"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-lg" />
            送出中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-lg" />
            送出諮詢需求
          </>
        )}
      </button>
    </form>
  );
}